import type { TenantDomain } from '../api/authoringApi'
import type { WebsiteDomain } from './website'

/**
 * One host this site's document states, as the Web addresses section
 * draws it.
 */
export interface StatedAddress {
  host: string
  /** The document's own word: this is the site's main address. */
  main: boolean
  /**
   * The index gives the host to another site — one of this tenant's, or
   * one outside it — so it does not answer for this one, whatever the
   * document says.
   */
  heldElsewhere: boolean
}

/** One host the author may pick in *Add a web address*. */
export interface OfferedAddress {
  host: string
  /** Already one of this site's, offered so the main address can be moved onto it. */
  onThisSite: boolean
}

export interface SiteAddresses {
  /** The document's hosts, in the document's order. */
  stated: StatedAddress[]
  /** Granted to the tenant and not answering for another site, by host. */
  offered: OfferedAddress[]
  /** Whether the tenant holds any granted host at all, in use or not. */
  anyGranted: boolean
}

/**
 * What the Web addresses section draws for one site, from the site's
 * document and the tenant's domain listing.
 *
 * **The document is the fact; the listing is the platform's index,
 * read beside it.** The chips are the document's, because a bind or a
 * release answers with the document and a chip that waited on the index
 * would lag the press that made it. The listing decides only two
 * things the document cannot: which hosts the tenant may bind at all,
 * and whether a host the document states answers for somebody else.
 *
 * Four states a host can be in, from this site's seat:
 *
 * - **granted** and bound to no site — offered;
 * - **bound here** — a chip, and offered too, so the main address can be
 *   moved onto it without releasing anything;
 * - **bound elsewhere** — to another site of the tenant, or to a site
 *   outside it — not offered, because the platform would refuse it;
 * - **stated, not held** — the document lists it and the index gives it
 *   to another site. The race the platform's guard cannot see, made
 *   visible rather than silent.
 *
 * The listing lags the document by a projection: a host bound a moment
 * ago is listed as stated by this site and answering for none, which is
 * *not* the fourth state, so a warning is drawn only when the index
 * names a holder.
 */
export function siteAddresses(
  site: string,
  domains: WebsiteDomain[] | undefined,
  listed: TenantDomain[] | undefined,
): SiteAddresses {
  const byHost = new Map((listed ?? []).map((entry) => [entry.host, entry]))
  const stated = (domains ?? []).map((domain) => {
    const entry = byHost.get(domain.host)
    return {
      host: domain.host,
      main: Boolean(domain.canonical),
      heldElsewhere: entry !== undefined && heldByAnother(site, entry),
    }
  })
  const offered = (listed ?? [])
    .filter((entry) => entry.granted && !heldByAnother(site, entry))
    // The document's word, not the index's, for the reason the chips are.
    .map((entry) => ({ host: entry.host, onThisSite: stated.some((address) => address.host === entry.host) }))
  return { stated, offered, anyGranted: (listed ?? []).some((entry) => entry.granted) }
}

/** The index gives `entry` to a site other than `site`. */
function heldByAnother(site: string, entry: TenantDomain): boolean {
  return entry.heldElsewhere || (entry.website !== undefined && entry.website !== site)
}

/**
 * Whether *Make this the main address* starts on for `host`.
 *
 * On for a site with no main address yet, because a site's first
 * address is almost always the one it should answer as; on for the host
 * that is already main, because binding it again with the switch off
 * would demote it. Off otherwise, so adding a second address never moves
 * the main one unless the author says so.
 */
export function mainByDefault(addresses: SiteAddresses, host: string | undefined): boolean {
  const main = addresses.stated.find((address) => address.main)
  return main === undefined || main.host === host
}
